import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import toast from 'react-hot-toast'
import emailTemplateService from '../services/emailTemplateService'
import UnsubscribeService from '../services/unsubscribeService'
import FirebaseUserDataService from '../services/firebaseUserData'
import { sanitizeEmailContent } from '../utils/sanitization'

const EmailTemplateLibrary = ({ selectionMode = false, onSelectTemplate, isCollapsed: externalCollapsed, onToggleCollapse, isDarkMode: externalDarkMode }) => {
  const { user } = useAuth()
  const { tenant } = useTenant()
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [businessProfile, setBusinessProfile] = useState(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (externalDarkMode !== undefined) {
      setIsDarkMode(externalDarkMode)
      return
    }

    const savedPreference = window.localStorage.getItem('marketGenieDarkMode')
    setIsDarkMode(savedPreference ? JSON.parse(savedPreference) : false)
  }, [externalDarkMode])

  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'cold_outreach',
    subject: '',
    body: '',
    variables: [],
    description: '',
    callToActionText: '',
    callToActionUrl: ''
  })

  const categories = [
    { id: 'all', name: 'All Templates', icon: '📚' },
    { id: 'cold_outreach', name: 'Cold Outreach', icon: '🎯' },
    { id: 'follow_up', name: 'Follow-up', icon: '📧' },
    { id: 'nurture', name: 'Nurture', icon: '🌱' },
    { id: 'sales', name: 'Sales', icon: '💰' },
    { id: 'onboarding', name: 'Onboarding', icon: '👋' },
    { id: 'custom', name: 'Custom', icon: '✨' }
  ]

  const availableVariables = [
    { name: 'firstName', example: 'John' },
    { name: 'lastName', example: 'Doe' },
    { name: 'email', example: 'john@company.com' },
    { name: 'companyName', example: 'Acme Corp' },
    { name: 'industry', example: 'Technology' },
    { name: 'jobTitle', example: 'CEO' },
    { name: 'phone', example: '+1 555-0100' },
    { name: 'website', example: 'https://acme.com' },
    { name: 'customField1', example: 'Custom Value' },
    { name: 'customField2', example: 'Custom Value 2' }
  ]

  // Load templates from Firestore
  useEffect(() => {
    const loadTemplates = async () => {
      if (!tenant?.id) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        
        // Fetch templates from Firestore
        const fetchedTemplates = await emailTemplateService.getTemplates(tenant.id)
        
        // If no templates exist, initialize default templates
        if (fetchedTemplates.length === 0) {
          await emailTemplateService.initializeDefaultTemplates(tenant.id)
          const defaultTemplates = await emailTemplateService.getTemplates(tenant.id)
          setTemplates(defaultTemplates)
          toast.success('Default templates loaded!')
        } else {
          setTemplates(fetchedTemplates)
        }
      } catch (error) {
        console.error('Error loading templates:', error)
        toast.error('Failed to load templates')
        setTemplates([])
      } finally {
        setIsLoading(false)
      }
    }

    loadTemplates()
  }, [tenant])

  // Load business profile for footer preview
  useEffect(() => {
    const loadBusinessProfile = async () => {
      if (!tenant?.id) return
      try {
        const profile = await FirebaseUserDataService.getBusinessProfile(tenant.id)
        setBusinessProfile(profile)
      } catch (error) {
        console.error('Error loading business profile:', error)
      }
    }
    loadBusinessProfile()
  }, [tenant])

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Please fill in all required fields')
      return
    }

    if (!tenant?.id) {
      toast.error('You must be logged in to save templates')
      return
    }

    try {
      // Extract variables from template body and subject
      const extractedVars = new Set()
      const regex = /\{\{(\w+)\}\}/g
      let match
      
      while ((match = regex.exec(templateForm.subject + ' ' + templateForm.body)) !== null) {
        extractedVars.add(match[1])
      }

      const templateData = {
        ...templateForm,
        variables: Array.from(extractedVars)
      }

      let savedTemplate
      if (isEditing) {
        // Update existing template
        savedTemplate = await emailTemplateService.updateTemplate(selectedTemplate.id, templateData, tenant.id)
        const updatedTemplates = templates.map(t => t.id === savedTemplate.id ? savedTemplate : t)
        setTemplates(updatedTemplates)
        toast.success('Template updated!')
      } else {
        // Create new template
        savedTemplate = await emailTemplateService.createTemplate(tenant.id, templateData)
        setTemplates([...templates, savedTemplate])
        toast.success('Template created!')
      }

      closeModal()
    } catch (error) {
      console.error('Error saving template:', error)
      toast.error('Failed to save template')
    }
  }

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return
    
    try {
      await emailTemplateService.deleteTemplate(templateId, tenant?.id)
      const updatedTemplates = templates.filter(t => t.id !== templateId)
      setTemplates(updatedTemplates)
      toast.success('Template deleted')
    } catch (error) {
      console.error('Error deleting template:', error)
      toast.error('Failed to delete template')
    }
  }

  const duplicateTemplate = async (template) => {
    if (!tenant?.id) {
      toast.error('You must be logged in to duplicate templates')
      return
    }

    try {
      const duplicateData = {
        name: `${template.name} (Copy)`,
        category: template.category,
        subject: template.subject,
        body: template.body,
        variables: template.variables || [],
        description: template.description || '',
        callToActionText: template.callToActionText || '',
        callToActionUrl: template.callToActionUrl || ''
      }
      
      const newTemplate = await emailTemplateService.createTemplate(tenant.id, duplicateData)
      setTemplates([...templates, newTemplate])
      toast.success('Template duplicated!')
    } catch (error) {
      console.error('Error duplicating template:', error)
      toast.error('Failed to duplicate template')
    }
  }

  const handleSelectTemplate = (template) => {
    if (selectionMode && onSelectTemplate) {
      onSelectTemplate(template)
      toast.success(`Template "${template.name}" selected!`)
    } else {
      openModal(template)
    }
  }

  const openModal = (template = null) => {
    if (template) {
      setIsEditing(true)
      setSelectedTemplate(template)
      setTemplateForm({
        name: template.name,
        category: template.category,
        subject: template.subject,
        body: template.body,
        variables: template.variables || [],
        description: template.description || '',
        callToActionText: template.callToActionText || '',
        callToActionUrl: template.callToActionUrl || ''
      })
    } else {
      setIsEditing(false)
      setSelectedTemplate(null)
      setTemplateForm({
        name: '',
        category: 'cold_outreach',
        subject: '',
        body: '',
        variables: [],
        description: '',
        callToActionText: '',
        callToActionUrl: ''
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setIsEditing(false)
    setSelectedTemplate(null)
  }

  const insertVariable = (varName) => {
    const variable = `{{${varName}}}`
    setTemplateForm({
      ...templateForm,
      body: templateForm.body + variable
    })
  }

  const previewTemplate = (template) => {
    let previewSubject = template.subject
    let previewBody = template.body
    
    // Replace variables with example values
    availableVariables.forEach(v => {
      const regex = new RegExp(`\\{\\{${v.name}\\}\\}`, 'g')
      previewSubject = previewSubject.replace(regex, v.example)
      previewBody = previewBody.replace(regex, v.example)
    })

    // Convert line breaks to <br> for display
    previewBody = previewBody.replace(/\n/g, '<br>')

    // Append call-to-action (if provided) before the footer
    const ctaText = (template.callToActionText || '').trim()
    const ctaUrl = (template.callToActionUrl || '').trim()
    if (ctaText || ctaUrl) {
      const ctaLabel = ctaText || ctaUrl
      const ctaHtml = ctaUrl
        ? `<p><a href="${ctaUrl}" target="_blank" rel="noopener noreferrer">${ctaLabel}</a></p>`
        : `<p>${ctaLabel}</p>`
      previewBody += `<br>${sanitizeEmailContent(ctaHtml)}`
    }

    // Append complete email footer with business profile data
    if (businessProfile && tenant?.id) {
      const completeFooter = UnsubscribeService.generateCompleteEmailFooter(
        tenant.id,
        'john@company.com', // Example email for preview
        businessProfile.senderInfo || businessProfile,
        businessProfile.businessInfo || businessProfile,
        'preview_campaign'
      )
      previewBody += completeFooter
    } else {
      // Fallback footer if no business profile loaded
      previewBody += `<br><br><div style="margin-top: 30px; padding: 20px; background-color: #f8fafc; border-top: 3px solid #14b8a6; font-family: Calibri, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; text-align: left; font-size: 12px; color: #6b7280;"><p><strong>Your Business Profile signature and footer will appear here</strong></p><p style="font-size: 11px; color: #9ca3af;">Configure your Business Profile to see your complete email footer preview</p></div>`
    }
    
    return {
      subject: previewSubject,
      body: previewBody
    }
  }

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const collapsed = externalCollapsed !== undefined ? externalCollapsed : false

  const panelClass = isDarkMode ? 'bg-gray-900 border border-gray-800 text-gray-100' : 'bg-white'
  const cardClass = isDarkMode ? 'bg-gray-800 border border-gray-700 text-gray-100' : 'bg-white'
  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`
  const secondaryTextClass = isDarkMode ? 'text-gray-400' : 'text-gray-600'
  const bodyTextClass = isDarkMode ? 'text-gray-100' : 'text-gray-900'

  return (
    <div className="space-y-4">
      {/* Selection Mode Banner */}
      {selectionMode && (
        <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-blue-700'}`}>📌 Click on a template to select it</p>
        </div>
      )}

      {/* Collapsible Header (only when used with external collapse control) */}
      {externalCollapsed !== undefined && onToggleCollapse && (
        <div 
          className={`flex justify-between items-center cursor-pointer p-3 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          onClick={onToggleCollapse}
        >
          <div className="flex items-center">
            <span className={`mr-3 text-lg transition-transform ${collapsed ? '' : 'rotate-90'}`}>
              ▶
            </span>
            <div>
              <h3 className="text-xl font-semibold text-genie-teal flex items-center">
                <span className="mr-2">📧</span>
                Email Templates
                <span className="ml-3 px-2.5 py-1 text-sm rounded-full bg-purple-100 text-purple-700">
                  {templates.length} templates
                </span>
              </h3>
              <p className={`text-sm mt-1 ${secondaryTextClass}`}>
                {collapsed ? 'Click to expand' : 'Click to collapse'} - Reusable email content
              </p>
            </div>
          </div>
          {!collapsed && (
            <button
              onClick={(e) => { e.stopPropagation(); openModal(); }}
              className="px-4 py-2 bg-genie-teal text-white rounded-lg hover:bg-genie-teal/80 transition-colors"
            >
              ✨ New Template
            </button>
          )}
        </div>
      )}

      {/* Content (only show if not collapsed) */}
      {!collapsed && (
        <>
          {/* Categories & Search */}
          <div className={`flex flex-col md:flex-row gap-4 items-center justify-between rounded-lg p-4 ${panelClass}`}>
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-sm ${
                    activeCategory === cat.id
                      ? 'bg-purple-600 text-white shadow-lg'
                      : isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-1">{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={inputClass + ' pl-10'}
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>

            {/* Create button for standalone mode */}
            {!externalCollapsed && !selectionMode && (
              <button
                onClick={() => openModal()}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium whitespace-nowrap"
              >
                ✨ Create Template
              </button>
            )}
          </div>

          {/* Templates Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className={secondaryTextClass}>Loading templates...</p>
              </div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">📧</div>
              <h3 className={`text-xl font-semibold mb-2 ${bodyTextClass}`}>No templates found</h3>
              <p className={`${secondaryTextClass} mb-4`}>
                {searchQuery ? 'Try a different search term' : 'Create your first email template to get started'}
              </p>
              {!selectionMode && (
                <button
                  onClick={() => openModal()}
                  className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium"
                >
                  ✨ Create Template
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => {
              const category = categories.find(c => c.id === template.category)
              return (
                <div
                  key={template.id}
                  className={`${cardClass} rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all ${
                    selectionMode ? 'cursor-pointer hover:border-2 hover:border-blue-400' : ''
                  }`}
                  onClick={() => selectionMode && handleSelectTemplate(template)}
                >
                  <div className={`p-3 bg-gradient-to-r ${
                    template.category === 'cold_outreach' ? 'from-slate-600 to-slate-700' :
                    template.category === 'follow_up' ? 'from-emerald-600 to-emerald-700' :
                    template.category === 'nurture' ? 'from-violet-600 to-violet-700' :
                    template.category === 'sales' ? 'from-amber-600 to-amber-700' :
                    template.category === 'onboarding' ? 'from-fuchsia-600 to-fuchsia-700' :
                    'from-slate-500 to-slate-600'
                  } text-white`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xl">{category?.icon}</span>
                      <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                        {template.usageCount || 0} uses
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className={`text-md font-bold mb-2 ${bodyTextClass}`}>{template.name}</h3>
                    <p className={`text-xs mb-3 line-clamp-2 ${secondaryTextClass}`}>{template.description || 'No description'}</p>
                    
                    <div className="mb-3">
                      <div className={`text-xs font-semibold mb-1 ${secondaryTextClass}`}>Subject:</div>
                      <div className={`text-xs ${secondaryTextClass} ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50'} p-2 rounded line-clamp-1`}>
                        {template.subject}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className={`text-xs font-semibold mb-1 ${secondaryTextClass}`}>Variables:</div>
                      <div className="flex flex-wrap gap-1">
                        {template.variables?.length > 0 ? template.variables.slice(0, 5).map(v => (
                          <span key={v} className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>
                            {`{{${v}}}`}
                          </span>
                        )) : <span className={`text-xs ${secondaryTextClass}`}>No variables</span>}
                        {template.variables?.length > 5 && (
                          <span className={`text-xs ${secondaryTextClass}`}>+{template.variables.length - 5} more</span>
                        )}
                      </div>
                    </div>

                    {(template.callToActionText || template.callToActionUrl) && (
                      <div className="mb-3">
                        <div className={`text-xs font-semibold mb-1 ${secondaryTextClass}`}>Call-to-Action:</div>
                        <div className={`text-xs p-2 rounded line-clamp-1 ${isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-50 text-green-700'}`}>
                          🎯 {template.callToActionText || template.callToActionUrl}
                        </div>
                      </div>
                    )}

                    {!selectionMode && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openModal(template); }}
                          className="flex-1 px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); duplicateTemplate(template); }}
                          className={`px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          title="Duplicate"
                        >
                          📋
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteTemplate(template.id); }}
                          className={`px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            </div>
          )}
        </>
      )}

      {/* Template Editor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`${cardClass} rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
            <div className="sticky top-0 bg-slate-700 px-6 py-4 text-white z-10">
              <h2 className="text-2xl font-bold">{isEditing ? 'Edit Template' : 'Create New Template'}</h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Template Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Template Name *</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Cold Outreach - B2B SaaS"
                  className={inputClass}
                />
              </div>

              {/* Category & Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Category *</label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className={inputClass}
                  >
                    {categories.filter(c => c.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Description</label>
                  <input
                    type="text"
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    placeholder="Brief description..."
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Subject Line */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Subject Line *</label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="e.g., Quick question about {{companyName}}"
                  className={inputClass}
                />
              </div>

              {/* Email Body */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Email Body *</label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder="Hi {{firstName}},&#10;&#10;Your message here...&#10;&#10;Best regards"
                  rows={12}
                  className={`${inputClass} font-mono text-sm`}
                />
              </div>

              {/* Call-to-Action */}
              <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-green-50 border-green-200'}`}>
                <label className={`block text-sm font-semibold mb-1 ${bodyTextClass}`}>🎯 Call-to-Action (End of Email Body)</label>
                <p className={`text-xs mb-3 ${secondaryTextClass}`}>Optional. Saved with the template, and pre-filled for you to add or adjust when the template is applied to a campaign email.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${secondaryTextClass}`}>Call-to-Action Text</label>
                    <input
                      type="text"
                      value={templateForm.callToActionText}
                      onChange={(e) => setTemplateForm({ ...templateForm, callToActionText: e.target.value })}
                      placeholder="e.g., Ready to get started? Book your free consultation!"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${secondaryTextClass}`}>Call-to-Action URL</label>
                    <input
                      type="url"
                      value={templateForm.callToActionUrl}
                      onChange={(e) => setTemplateForm({ ...templateForm, callToActionUrl: e.target.value })}
                      placeholder="https://your-landing-page.com"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>

              {/* Available Variables */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Insert Variable</label>
                <div className="flex flex-wrap gap-2">
                  {availableVariables.map(v => (
                    <button
                      key={v.name}
                      onClick={() => insertVariable(v.name)}
                      className={`px-3 py-1 rounded-lg text-sm font-mono ${isDarkMode ? 'bg-indigo-900 text-indigo-100 hover:bg-indigo-800' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                      title={`Example: ${v.example}`}
                    >
                      {`{{${v.name}}}`}
                    </button>
                  ))}
                </div>
                <p className={`text-xs mt-2 ${secondaryTextClass}`}>Click to insert variable into email body. Variables will be replaced with actual contact data.</p>
              </div>

              {/* Preview */}
              {(templateForm.subject || templateForm.body) && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${bodyTextClass}`}>Preview (with example data)</label>
                  <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="mb-2">
                      <span className={`text-xs font-semibold ${secondaryTextClass}`}>Subject:</span>
                      <div className={`text-sm mt-1 ${bodyTextClass}`}>
                        {previewTemplate({ subject: templateForm.subject, body: templateForm.body, callToActionText: templateForm.callToActionText, callToActionUrl: templateForm.callToActionUrl }).subject}
                      </div>
                    </div>
                    <div>
                      <span className={`text-xs font-semibold ${secondaryTextClass}`}>Body:</span>
                      <div 
                        className={`text-sm mt-1 ${bodyTextClass}`}
                        dangerouslySetInnerHTML={{ __html: previewTemplate({ subject: templateForm.subject, body: templateForm.body, callToActionText: templateForm.callToActionText, callToActionUrl: templateForm.callToActionUrl }).body }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`sticky bottom-0 px-6 py-4 flex justify-end gap-3 border-t ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50'}`}>
              <button
                onClick={closeModal}
                className={`px-6 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium"
              >
                {isEditing ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmailTemplateLibrary
